<template>
  <div class="add-task">
    <h1 v-if="hasTitle">{{ $t('taskManager') }}</h1>
    <div class="table-responsive" v-if="tasks.length">
      <b-table
        class="mb-0"
        :small="small"
        :hover="hover"
        :items="tasks"
        :fields="fields"
      >
        <template slot="title" slot-scope="{ item }">
          <slot name="task-name" :task="item">
            <div class="pt-3 pb-3 small font-weight-bold">
              <svg-icon
                :icon="`${item.title.toLowerCase().replace(/\s/g, '')}-round`"
                class="icon-2x mr-2"
              />
              <span
                class="align-middle text-uppercase"
                data-cy="task-manager-list-item"
              >
                {{ item.title }}
              </span>
            </div>
          </slot>
        </template>
        <template slot="status" slot-scope="{ item }">
          <div class="pt-3 pb-3 badge-wrap">
            <span
              class="badge text-uppercase align-middle"
              :class="getBadgeVariant(item.status)"
            >
              {{ $t(`statuses.${item.status}`) }}
            </span>
          </div>
        </template>
        <template slot="actions" slot-scope="{ item }">
          <slot name="actions-td" :task="item">
            <div class="pt-3 pb-3 small text-right">
              <router-link
                :to="
                  `/campaigns/${campaignId}/task-edit/${item.title.toLowerCase()}`
                "
                class="btn-link-alt-primary"
              >
                <svg-icon icon="pencil" class="mr-1" />
                <span>{{ $t('edit') }}</span>
              </router-link>
            </div>
          </slot>
        </template>
      </b-table>
    </div>
    <div class="text-center text-lighten" v-else>{{ $t('noTasks') }}</div>
    <b-dropdown
      v-if="notAddedTasks.length"
      class="add-task__manager"
      variant="link"
      left
      no-caret
    >
      <template slot="button-content">
        <svg-icon icon="plus-round" class="icon-lg mr-2" />
        <span class="small font-weight-bold align-middle">
          {{ $t('addNewTask') | upperCase }}
        </span>
      </template>
      <div class="drop-list">
        <b-dropdown-item
          v-for="task in notAddedTasks"
          data-cy="task-manager-item"
          :key="task.id"
          @click="goToTaskCreation(task)"
        >
          <svg-icon
            :icon="`${task.title.toLowerCase().replace(/\s/g, '')}`"
            class="icon-sm mr-1"
            :class="{
              [`${task.title.toLowerCase().replace(/\s/g, '')}-color`]: true
            }"
          />
          <span class="align-middle text-capitalize">{{ task.title }}</span>
        </b-dropdown-item>
      </div>
    </b-dropdown>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  props: {
    hasTitle: {
      type: Boolean,
      default: false
    },
    fields: {
      type: Array,
      default() {
        return [
          {
            label: this.$tc('task', 2),
            key: 'title',
            thClass:
              'border-top-0 small font-weight-bold text-nowrap text-capitalize'
          },
          {
            label: this.$tc('status', 1),
            key: 'status',
            thClass:
              'border-top-0 small font-weight-bold text-nowrap text-center'
          },
          {
            label: '',
            name: 'actions',
            key: 'actions',
            thClass: 'border-top-0 small font-weight-bold text-nowrap',
            tdClass: 'align-middle'
          }
        ];
      }
    },
    tasks: {
      type: Array,
      default: () => []
    },
    small: {
      type: Boolean,
      default: false
    },
    hover: {
      type: Boolean,
      default: false
    },
    hasAccessForCreate: {
      type: Boolean,
      default: false
    },
    campaignListId: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      allTasks: []
    };
  },

  methods: {
    getBadgeVariant(status) {
      return {
        'badge-success': status === 'active',
        'badge-info': status === 'pending',
        'badge-danger': status === 'closed'
      };
    },
    goToTaskCreation(task) {
      if (this.hasAccessForCreate) {
        const campaignId =
          this.$route.name === 'campaign-add' ||
          this.$route.name === 'campaign-edit'
            ? this.$route.params.id
            : this.campaignListId;
        this.$router.push(
          `/campaigns/${campaignId}/task-add/${task.title.toLowerCase()}`
        );
      } else {
        this.$notify('alert', {
          text: 'You need to create campaign before.',
          variant: 'danger',
          time: 5000
        });
      }
    },
    ...mapActions({
      fetchTaskTypes: 'bounty/fetchTaskTypes'
    })
  },

  computed: {
    notAddedTasks() {
      return this.allTasks
        .filter(el => {
          return !this.tasks.find(
            obj => obj.title.toLowerCase() === el.title.toLowerCase()
          );
        })
        .map(el => ({
          ...el,
          title: el.id === 'vk' ? el.title : el.title.toLowerCase()
        }));
    },
    campaignId() {
      return this.$route.params.id;
    }
  },

  created() {
    this.fetchTaskTypes().then(tasks => {
      this.allTasks = tasks;
    });
  }
};
</script>

<style lang="scss">
.add-task__manager {
  .btn-link {
    color: $primary;
    padding-left: 0;
    text-decoration: none;
  }
}

.table tbody td {
  > div {
    white-space: nowrap;
  }
}

.badge-wrap {
  font-size: 0;
}
</style>

<style lang="scss" scoped>
.drop-list {
  max-height: 250px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
